import { useState, useEffect, useMemo, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useOrdersGet } from '@pos/api-sdk';
import { useUserOrders } from '../hooks/queries';
import { getApiBase, getApiHeaders, getTenantIdFromUrl } from '../api/apiUtils';
import { useTenant } from '../contexts/TenantContext';
import { Loading } from './Loading';
import { SectionCard } from './SectionCard';
import type {
  OrdersGetByUser200DataItem,
  OrdersGet200DataOrderItemsItem,
} from '@pos/api-sdk';
import { formatVariantSnapshot } from '@pos/shared/utils';
import { formatTwdFromCents } from '@pos/shared/money';

/** 展開時依訂單 ID 取得詳情並顯示購買商品與數量 */
function OrderDetailContent({
  orderId,
  isExpanded,
}: {
  orderId: string;
  isExpanded: boolean;
}) {
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;
  const apiHeaders = getApiHeaders(tenantId);

  const { data, isLoading } = useOrdersGet(orderId, {
    swr: { enabled: isExpanded && !!tenantId },
    fetch: { headers: apiHeaders },
  });

  if (!isExpanded) return null;
  if (isLoading) {
    return (
      <div className="py-3">
        <Loading />
      </div>
    );
  }
  const body = data?.data;
  const detail =
    body && typeof body === 'object' && 'data' in body ? body.data : undefined;
  const items: OrdersGet200DataOrderItemsItem[] = detail?.order_items ?? [];
  const totalTwd = detail?.total_twd ?? 0;

  return (
    <div className="text-sm space-y-2">
      <div className="font-medium text-gray-700 mb-1">購買商品</div>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li
            key={String(item.id)}
            className="flex justify-between items-start gap-2 text-gray-800"
          >
            <span>
              {item.product_name_snapshot || '商品'}
              {(item as { variant_snapshot?: Record<string, string> | null })
                .variant_snapshot && (
                <span className="text-gray-500 ml-1">
                  ·{' '}
                  {formatVariantSnapshot(
                    (
                      item as {
                        variant_snapshot?: Record<string, string> | null;
                      }
                    ).variant_snapshot
                  )}
                </span>
              )}
              <span className="text-gray-500 ml-1">x {item.quantity}</span>
            </span>
            <span className="shrink-0">
              {formatTwdFromCents(
                item.total_twd ?? item.unit_price_twd * item.quantity
              )}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between pt-2 border-t border-gray-300 font-semibold text-gray-900">
        <span>總計</span>
        <span>{formatTwdFromCents(totalTwd)}</span>
      </div>
    </div>
  );
}

interface OrderHistoryProps {
  userId: string;
  highlightOrderNumber?: string; // 要標記的訂單編號
}

export function OrderHistory({
  userId,
  highlightOrderNumber,
}: OrderHistoryProps) {
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const tenantId = contextTenantId || urlTenantId;

  const [page, setPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // 使用 SWR hook 取得訂單列表
  const limit = 10;
  const {
    orders,
    pagination: apiPagination,
    isLoading: loading,
    error: queryError,
  } = useUserOrders(userId, {
    page,
    limit,
    sortBy: 'id',
    sortDir: 'desc',
  });

  const ordersList = useMemo<OrdersGetByUser200DataItem[]>(
    () => orders ?? [],
    [orders]
  );
  const pagination = apiPagination
    ? {
        page: apiPagination.page,
        limit: apiPagination.limit,
        total: apiPagination.total,
        total_pages: apiPagination.total_pages,
      }
    : {
        page,
        limit,
        total: ordersList.length,
        total_pages: 1,
      };

  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : '載入失敗'
    : null;

  // 如果有要標記的訂單編號，自動展開該訂單
  useEffect(() => {
    if (highlightOrderNumber && ordersList.length > 0) {
      const highlightedOrder = ordersList.find(
        order => order.order_number === highlightOrderNumber
      );
      if (highlightedOrder) {
        setExpandedOrders(new Set([highlightedOrder.id]));
      }
    }
  }, [highlightOrderNumber, ordersList]);

  const formatDate = (dateString: string) => {
    return formatInTimeZone(
      new Date(dateString),
      'Asia/Taipei',
      'yyyy/MM/dd HH:mm'
    );
  };

  /** 付款狀態標籤：待付款、已付款等（購買記錄只顯示付款＋出貨，不顯示「已成立」） */
  const getPaymentStatusLabel = (paymentStatus: string | null | undefined) => {
    if (!paymentStatus) return null;
    if (paymentStatus === '待付款' || paymentStatus === '未付款')
      return { text: '待付款', className: 'bg-amber-100 text-amber-800' };
    if (paymentStatus === '已付款')
      return { text: '已付款', className: 'bg-green-100 text-green-800' };
    return { text: paymentStatus, className: 'bg-gray-100 text-gray-800' };
  };

  /**
   * 出貨管理狀態 → 會員中心顯示狀態
   * 出貨狀態僅此六種：待處理、處理中、已出貨、已送達、已取貨、已退回
   */
  const getShippingStatusLabel = (
    shippingStatus: string | null | undefined
  ) => {
    if (!shippingStatus) return null;
    const map: Record<string, { text: string; className: string }> = {
      待處理: { text: '訂單處理中', className: 'bg-gray-100 text-gray-800' },
      處理中: { text: '商家備貨中', className: 'bg-gray-100 text-gray-800' },
      已出貨: { text: '商家已出貨', className: 'bg-gray-100 text-gray-800' },
      已送達: { text: '待取貨', className: 'bg-amber-100 text-amber-800' },
      已取貨: { text: '已取貨', className: 'bg-green-100 text-green-800' },
      已退回: { text: '已退回', className: 'bg-red-100 text-red-800' },
    };
    return (
      map[shippingStatus] ?? {
        text: shippingStatus,
        className: 'bg-gray-100 text-gray-800',
      }
    );
  };

  /** 格式化面交/取貨日為台北時間顯示 */
  const formatPickupDate = (dateStr: string | null | undefined) => {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
    try {
      return formatInTimeZone(
        new Date(dateStr + 'T12:00:00Z'),
        'Asia/Taipei',
        'yyyy/MM/dd'
      );
    } catch {
      return dateStr;
    }
  };

  /** 是否可顯示「再次付款」：預購／線上商店／線上訂單、待付款、未逾時（與 preorder 邏輯一致） */
  const canShowPayAgain = useCallback((order: OrdersGetByUser200DataItem) => {
    const channel = order.channel ?? '';
    if (
      channel !== 'preorder' &&
      channel !== 'online_shopping' &&
      channel !== 'online_order'
    )
      return false;
    const paymentStatus =
      order.payment_status ??
      (order as { payment_status?: string }).payment_status;
    if (paymentStatus !== '待付款' && paymentStatus !== '未付款') return false;
    const expiresAt = (order as { payment_expires_at?: string })
      .payment_expires_at;
    if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) return false;
    return true;
  }, []);

  /** 再次付款：呼叫 payment/request 後導向金流 */
  const [payAgainLoading, setPayAgainLoading] = useState<string | null>(null);
  const handlePayAgain = useCallback(
    async (e: React.MouseEvent, order: OrdersGetByUser200DataItem) => {
      e.stopPropagation();
      if (!tenantId) return;
      setPayAgainLoading(order.id);
      try {
        const apiBase = getApiBase();
        const res = await fetch(`${apiBase}/api/preorders/payment/request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getApiHeaders(tenantId),
          },
          body: JSON.stringify({
            orderId: order.id,
            paymentProvider: 'line_pay',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data?.message ?? `請求失敗 (${res.status})`;
          alert(msg);
          return;
        }
        const payData = data?.data ?? data;
        const formActionUrl =
          payData.formAction ?? payData.paymentUrl ?? payData.payment_url;
        const formDataPayload = payData.formData ?? payData.form_data;
        if (
          formActionUrl &&
          formDataPayload &&
          Object.keys(formDataPayload).length > 0
        ) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = formActionUrl;
          Object.entries(formDataPayload).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = String(v);
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
        if (formActionUrl) {
          window.location.href = formActionUrl;
        }
      } catch (err) {
        alert(err instanceof Error ? err.message : '再次付款失敗');
      } finally {
        setPayAgainLoading(null);
      }
    },
    [tenantId]
  );

  const toggleOrder = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  if (loading) {
    return (
      <SectionCard title="購買記錄">
        <Loading />
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="購買記錄">
        <div className="text-red-600 text-center py-4">{error}</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="購買記錄">
      {ordersList.length === 0 ? (
        <div className="text-center text-gray-500 py-8">尚無訂單記錄</div>
      ) : (
        <>
          <div className="space-y-3">
            {ordersList.map(order => {
              const paymentStatus = order.payment_status ?? '';
              const isExpanded = expandedOrders.has(order.id);
              const isHighlighted = highlightOrderNumber === order.order_number;
              const pickupDateStr =
                order.pickup_date &&
                order.channel !== 'online_order' &&
                order.channel !== 'online_shopping'
                  ? formatPickupDate(order.pickup_date)
                  : null;

              return (
                <div
                  key={order.id}
                  className={`border rounded-lg hover:bg-gray-50 transition-colors ${
                    isHighlighted
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200'
                  }`}
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleOrder(order.id)}
                  >
                    {/* 面交日：有則顯示在訂單上方、強調 */}
                    {pickupDateStr && (
                      <div className="mb-2 rounded bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-800">
                        面交日：{pickupDateStr}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                      <div className="min-w-0 overflow-x-auto">
                        <span className="font-mono text-sm text-gray-600 whitespace-nowrap">
                          {order.order_number}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatTwdFromCents(order.total_twd)}
                        </div>
                        {(order.discount_twd > 0 ||
                          order.points_discount_twd > 0) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {order.discount_twd > 0 &&
                              `折扣: -${formatTwdFromCents(order.discount_twd)}`}
                            {order.discount_twd > 0 &&
                              order.points_discount_twd > 0 &&
                              ' / '}
                            {order.points_discount_twd > 0 &&
                              `點數: -${formatTwdFromCents(order.points_discount_twd)}`}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      {getPaymentStatusLabel(paymentStatus) && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            getPaymentStatusLabel(paymentStatus)!.className
                          }`}
                        >
                          {getPaymentStatusLabel(paymentStatus)!.text}
                        </span>
                      )}
                      {/* 僅已付款訂單顯示出貨狀態（未付款不會進出貨管理，不顯示待出貨） */}
                      {paymentStatus === '已付款' &&
                        getShippingStatusLabel(order.shipping_status) && (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              getShippingStatusLabel(order.shipping_status)!
                                .className
                            }`}
                          >
                            {
                              getShippingStatusLabel(order.shipping_status)!
                                .text
                            }
                          </span>
                        )}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500 flex-wrap gap-1">
                      <span>
                        {formatDate(
                          order.created_at_taipei || order.created_at
                        )}
                      </span>
                      <span className="text-blue-600">
                        {isExpanded ? '收起' : '查看詳情'} ▼
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-gray-50">
                      {/* 再次付款僅顯示在收合詳情內 */}
                      {canShowPayAgain(order) && (
                        <div className="mb-3">
                          <button
                            type="button"
                            onClick={e => handlePayAgain(e, order)}
                            disabled={payAgainLoading === order.id}
                            className="w-full py-2 px-4 rounded font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {payAgainLoading === order.id
                              ? '處理中…'
                              : '再次付款'}
                          </button>
                        </div>
                      )}
                      <OrderDetailContent
                        orderId={order.id}
                        isExpanded={isExpanded}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                第 {pagination.page} / {pagination.total_pages} 頁，共{' '}
                {pagination.total} 筆
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一頁
                </button>
                <button
                  onClick={() =>
                    setPage(p => Math.min(pagination.total_pages, p + 1))
                  }
                  disabled={page === pagination.total_pages}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一頁
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}
